import * as _ from 'lodash-es';
import { createContext, useContext, useEffect, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './useWidthExcludingSidebarSelector.module.scss';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const StoryDemoWidthContext = createContext<number | undefined>(
  undefined,
);

const SIDEBAR_WIDTH = parseInt(styles.sidebarWidth, 10);

const getWidthExcludingSidebar = (width: number, isSidebarOpen: boolean) =>
  width - (isSidebarOpen ? SIDEBAR_WIDTH : 0);

/**
 * Returns the window width minus the sidebar width when it's open. Takes a selector function
 * that transforms the calculated width into a value of type T. Using a selector function
 * prevents unnecessary re-renders by only updating when the transformed value changes,
 * not on every resize event.
 *
 * @example
 * // Returns true if width (excluding sidebar) is less than 768px
 * const isNarrow = useWidthExcludingSidebarSelector(width => width < 768);
 */
const useWidthExcludingSidebarSelector = <T>(
  selector: (width: number) => T,
): T => {
  const storyDemoWidth = useContext(StoryDemoWidthContext);
  const isSidebarOpen = useIsSidebarOpen();
  const [state, setState] = useState(() =>
    selector(getWidthExcludingSidebar(window.innerWidth, isSidebarOpen)),
  );

  const handleResize = useEffectEvent(() => {
    const next = selector(
      getWidthExcludingSidebar(window.innerWidth, isSidebarOpen),
    );

    setState((current) => (_.isEqual(current, next) ? current : next));
  });

  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isSidebarOpen]);

  return storyDemoWidth == null ? state : selector(storyDemoWidth);
};

export default useWidthExcludingSidebarSelector;
